import { useEffect, useMemo, useRef } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { WarningLockIcon } from "components/icons";
import Loader from "components/Loader";
import ModalConfirmation from "components/ModalConfirmation";

import { useGetPendingEOD } from "./useGetPendingEOD";
import { useBusinessUnitStore, usePendingEODStore } from "@type/store";
import { useTranslationContext } from "context/TranslationContext";
import { replaceSegmentsFromRoutes } from "helpers/replaceSegmentsFromRoutes";

import { buSlugParam, routePaths } from "routes/constants";

interface PendingEODDataWrapperProps {
  children: React.ReactNode;
}

const MIN_REFRESH_DATE_IN_MINUTE = 5;

const PendingEODDataWrapper = ({ children }: PendingEODDataWrapperProps) => {
  const selectedBU = useBusinessUnitStore((state) => state.selectedBU);
  const location = useLocation();
  const refetchCountPendingEOD = usePendingEODStore(
    (state) => state.refetchCountPendingEOD
  );
  const lastRefreshDateRef = useRef(Date.now());
  const { t } = useTranslationContext();
  const navigate = useNavigate();
  const onRefetchPendingEOD = usePendingEODStore(
    (state) => state.onRefetchPendingEOD
  );

  const { isLoadingGetPendingEOD, pendingEOD, refetchPendingEOD } =
    useGetPendingEOD();

  const isInPendingRoute = useMemo(() => {
    const matchParent = matchPath(
      { path: routePaths.root.pBuSlug.pendingReport.root, end: true },
      location.pathname
    );
    const matchTicket = matchPath(
      { path: routePaths.root.pBuSlug.pendingReport.tickets.root, end: true },
      location.pathname
    );
    const matchReportingLine = matchPath(
      { path: routePaths.root.pBuSlug.pendingReport.reporting.root, end: true },
      location.pathname
    );
    const matchRoot = matchPath(
      { path: routePaths.root.root, end: true },
      location.pathname
    );

    return (
      !!matchParent || !!matchTicket || !!matchReportingLine || !!matchRoot
    );
  }, [location.pathname]);

  const handleOpenPendingPage = () => {
    if (!selectedBU?.idUnique) return;
    navigate(
      replaceSegmentsFromRoutes({
        route: routePaths.root.pBuSlug.pendingReport.root,
        params: [buSlugParam],
        replacer: [selectedBU.idUnique],
      })
    );
  };

  useEffect(() => {
    const differentDate =
      (Date.now() - lastRefreshDateRef.current) / (60 * 1000);

    if (differentDate >= MIN_REFRESH_DATE_IN_MINUTE) {
      onRefetchPendingEOD();
      lastRefreshDateRef.current = Date.now();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (refetchCountPendingEOD > 0) {
      refetchPendingEOD();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchCountPendingEOD]);

  if (isLoadingGetPendingEOD) {
    return <Loader />;
  }

  return (
    <>
      {children}

      <ModalConfirmation
        open={
          !isLoadingGetPendingEOD &&
          pendingEOD?.data?.status === "UNSUBMITTED" &&
          !isInPendingRoute
        }
        title={t("pending_eod_reports")}
        submitLabel={t("complete_now")}
        color="mustard"
        icon={<WarningLockIcon className="text-mustard-600" />}
        message={t("eod_complete_now.message")}
        onSubmit={handleOpenPendingPage}
      />
    </>
  );
};

export default PendingEODDataWrapper;
